import { z } from "zod";

export const PERMISSIONS_VERSION = 1 as const;

export const PermissionScope = {
  NONE: "NONE",
  ASSIGNED: "ASSIGNED",
  TEAM: "TEAM",
  ALL: "ALL",
} as const;

export type PermissionScopeEnum = (typeof PermissionScope)[keyof typeof PermissionScope];

export const PermissionKey = {
  CLIENTS_READ: "clients.read",
  CLIENTS_WRITE: "clients.write",
  // TRE-2043 — nieodwracalne usunięcie danych osobowych klientki (art. 17 RODO):
  // tombstone tożsamości, kasacja pomiarów/zdjęć/aktywności, ucięcie sesji.
  // Osobno od `clients.write`, bo to inna klasa ryzyka niż edycja profilu —
  // brak backupu per-klientka. Jedyny sensowny scope to ALL (operacja na
  // koncie, nie na przypisaniu). Domyślnie: ACCOUNT_OWNER, ADMINISTRATION.
  CLIENTS_ERASE: "clients.erase",
  CHAT_READ: "chat.read",
  CHAT_WRITE: "chat.write",
  CHAT_ALERTS_READ: "chat.alerts.read",
  PROGRAMS_MANAGE: "programs.manage",
  NUTRITION_MANAGE: "nutrition.manage",
  NUTRITION_INGREDIENTS_APPROVE: "nutrition.ingredients.approve",
  // Edycja wpisu w katalogu składników. Osobno od `approve`, bo to inna decyzja:
  // zatwierdzanie przepuszcza produkt zgłoszony z apki klientki do katalogu,
  // edycja poprawia makro produktu, który już siedzi w tysiącach jadłospisów.
  // Katalog jest globalny (brak kolumny tenantowej) — jedyny sensowny scope to ALL.
  NUTRITION_INGREDIENTS_MANAGE: "nutrition.ingredients.manage",
  PRODUCTS_READ: "products.read",
  PRODUCTS_RESOURCES_READ: "products.resources.read",
  PRODUCTS_RESOURCES_WRITE: "products.resources.write",
  PRODUCTS_MANAGE: "products.manage",
  SALES_READ: "sales.read",
  SALES_MANAGE: "sales.manage",
  SALES_SETTLEMENTS_READ: "sales.settlements.read",
  // TRE-2017 — koszyki sprzedażowe (`checkout_variant`): włączanie/wyłączanie,
  // data ważności i generowanie indywidualnego kodu zniżkowego dla klientki.
  // Osobno od `products.manage`, bo to codzienna operacja sprzedaży na gotowym
  // koszyku, a nie edycja katalogu (produkty, ceny, ustawienia, lejki).
  // Jedyny sensowny scope to ALL — wariant wisi na produkcie tenanta, nie na
  // przypisanych klientkach. Domyślnie: ACCOUNT_OWNER, ADMINISTRATION, SALES_REP.
  SALES_CHECKOUT_MANAGE: "sales.checkout.manage",
  // TRE-1862 — agregaty przychodowe czytane przez salesAgenta. Osobno od
  // `sales.read`, które ma cały dział sprzedaży: to inny poziom danych.
  // W katalogu wyłącznie ACCOUNT_OWNER (scope ALL) — patrz migracja
  // 20260809000000_sales_analytics_permission.
  SALES_ANALYTICS_READ: "sales.analytics.read",
  SUPPORT_READ: "support.read",
  SUPPORT_MANAGE: "support.manage",
  SUPPORT_RESOURCES_MANAGE: "support.resources.manage",
  LANDINGS_MANAGE: "landings.manage",
  TEAM_READ: "team.read",
  TEAM_MANAGE: "team.manage",
  TEAM_PERMISSIONS_MANAGE: "team.permissions.manage",
  APP_CONFIG_MANAGE: "appConfig.manage",
  // TRE-2126 — centrum pomocy dla klientek (instrukcje i „Co nowego”). Dwa
  // klucze, bo to dwie różne decyzje: redakcja materiału i wypuszczenie go
  // klientkom. `manage` bez `publish` redaguje, ale nie przestawi wpisu na
  // `published` — bramka stoi w `with check` polityki, nie tylko w panelu.
  // Jedyny sensowny scope to ALL: centrum pomocy nie wisi na przypisanych
  // klientkach. Domyślnie: `manage` → ACCOUNT_OWNER, ADMINISTRATION;
  // `publish` → ACCOUNT_OWNER (migracja 20260910120000_help_center_content).
  HELP_CENTER_MANAGE: "helpCenter.manage",
  HELP_CENTER_PUBLISH: "helpCenter.publish",
  AUTOMATION_MANAGE: "automation.manage",
  MARKETING_READ: "marketing.read",
  CAMPAIGNS_MANAGE: "campaigns.manage",
  SURVEYS_MANAGE: "surveys.manage",
  ENGAGEMENT_MANAGE: "engagement.manage",
  ENGAGEMENT_BADGES_MANAGE: "engagement.badges.manage",
  AGENTS_USE: "agents.use",
  TEAM_STATS_READ: "team.stats.read",
  TEAM_CHAT_METRICS_READ: "team.chatMetrics.read",
} as const;

export type PermissionKeyEnum = (typeof PermissionKey)[keyof typeof PermissionKey];
export type PermissionMap = Partial<Record<PermissionKeyEnum, PermissionScopeEnum>>;
export type PermissionsVersion = typeof PERMISSIONS_VERSION;

export const permissionScopeSchema = z.enum(PermissionScope);
export const permissionKeySchema = z.enum(PermissionKey);
export const permissionMapSchema = z.partialRecord(permissionKeySchema, permissionScopeSchema);

const permissionScopeRank: Record<PermissionScopeEnum, number> = {
  [PermissionScope.NONE]: 0,
  [PermissionScope.ASSIGNED]: 1,
  [PermissionScope.TEAM]: 2,
  [PermissionScope.ALL]: 3,
};

export const parsePermissionMap = (value: unknown): PermissionMap => {
  // Klucz spoza enuma (świeży wpis w katalogu, starsza wersja apki) nie może
  // zerować całej mapy — bez tego każde rozszerzenie katalogu wylogowywało
  // funkcjonalnie cały personel do czasu deployu apki. Filtrujemy wpisy
  // pojedynczo zamiast walidować mapę w całości.
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(value).filter(
      ([key, scope]) => permissionKeySchema.safeParse(key).success && permissionScopeSchema.safeParse(scope).success,
    ),
  ) as PermissionMap;
};

export const getPermissionScope = (permissions: PermissionMap, key: PermissionKeyEnum): PermissionScopeEnum =>
  permissions[key] ?? PermissionScope.NONE;

export const hasPermission = (
  permissions: PermissionMap,
  key: PermissionKeyEnum,
  minimumScope: PermissionScopeEnum = PermissionScope.ASSIGNED,
): boolean => permissionScopeRank[getPermissionScope(permissions, key)] >= permissionScopeRank[minimumScope];

export const maxPermissionScope = (first: PermissionScopeEnum, second: PermissionScopeEnum): PermissionScopeEnum =>
  permissionScopeRank[first] >= permissionScopeRank[second] ? first : second;
